import classNames from 'classnames'
import React, { useCallback } from 'react'
import { Cell, TableRowProps as InternalTableRowProps } from 'react-table'
import { makeStyles } from 'tss-react/mui'
import { ExtendableStyles } from 'utils-next/types'

import { TableRow as TableRowInternal } from '@mui/material'
import yellow from '@mui/material/colors/yellow'

import { TableProps } from './Table.component'
import { TableRowCell } from './TableRowCell.component'

export type TableRowProps<DataType extends object> = ExtendableStyles &
  InternalTableRowProps &
  Pick<TableProps<DataType>, 'onRowSelect' | 'shouldHighlight'> & {
    cells: Cell<DataType>[]
    rowOriginalData: DataType
  }

export const TableRow = React.memo(
  ({ className, style, cells, onRowSelect, rowOriginalData, shouldHighlight, ...otherProps }: TableRowProps<any>) => {
    const { classes } = useStyles()
    const handleClick = useCallback(() => {
      if (onRowSelect) {
        onRowSelect(rowOriginalData)
      }
    }, [onRowSelect, rowOriginalData])
    const highlight = shouldHighlight ? shouldHighlight(rowOriginalData) : false
    return (
      <TableRowInternal
        hover={!!onRowSelect}
        className={classNames(
          className,
          classes.tableRow,
          onRowSelect && classes.tableRowSelectable,
          highlight && classes.tableRowHighlight
        )}
        style={style}
        {...otherProps}
      >
        {cells.map((props) => (
          // eslint-disable-next-line react/jsx-key
          <TableRowCell {...props.getCellProps()} onClick={handleClick} {...props} />
        ))}
      </TableRowInternal>
    )
  }
)

TableRow.displayName = 'TableRow'

const useStyles = makeStyles()({
  internalWrapper: {
    display: 'flex',
    width: '100%',
  },
  tableRow: {
    ['&:last-child']: {
      borderBottom: 0,
    },
  },
  tableRowSelectable: {
    cursor: 'pointer',
  },
  tableRowHighlight: {
    backgroundColor: yellow[50],
  },
})
